import type { ReactNode } from "react";
import { useEffect } from "react";
import { useScrollThumb } from "hooks/useScrollThumb";
import { DrawerCloseIcon } from "./nav.icons";

type CabinetDrawerProps = {
  open: boolean;
  onClose: () => void;
  children: ReactNode;
};

/**
 * Below the sidebar breakpoint the menu becomes an overlay behind the burger,
 * as in the design. The prototype moves the real nodes into the drawer; React
 * renders them here instead, which comes to the same markup.
 *
 * The scroll lock is a class on the host page's body: the widget is mounted
 * inside that page and cannot reach it from its own stylesheet.
 */
export const CabinetDrawer = ({ open, onClose, children }: CabinetDrawerProps) => {
  // The design's `#pc-drawer-thumb`; rebound on open, while the scroller has a size to measure.
  const { listRef, thumbRef } = useScrollThumb(open);

  useEffect(() => {
    if (!open) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };

    document.addEventListener("keydown", handleKeyDown);
    document.body.classList.add("is-drawer-open");

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.body.classList.remove("is-drawer-open");
    };
  }, [open, onClose]);

  return (
    <aside className="pcdr" id="pc-drawer" hidden={!open}>
      <div className="pcdr__back" aria-hidden="true" onClick={onClose} />

      <div className="pcdr__panel" role="dialog" aria-modal="true" aria-label="Account menu">
        <div className="pcdr__hd">
          <button type="button" className="pcdr__x" aria-label="Close menu" onClick={onClose}>
            <DrawerCloseIcon />
          </button>
        </div>

        <div className="pcdr__wrap">
          <div className="pcdr__scroll" ref={listRef}>
            {children}
          </div>
          <div className="pcdr__thumb" ref={thumbRef} />
        </div>
      </div>
    </aside>
  );
};
